import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Minus, Plus, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import ProductReviews from "@/components/ProductReviews";

const CADENCES = [
  { v: "weekly", l: "Every week" },
  { v: "monthly", l: "Every month" },
  { v: "2m", l: "Every 2 months" },
  { v: "3m", l: "Every 3 months" },
  { v: "4m", l: "Every 4 months" },
  { v: "5m", l: "Every 5 months" },
  { v: "6m", l: "Every 6 months" },
  { v: "yearly", l: "Every year" },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [mode, setMode] = useState("onetime"); // onetime | subscribe
  const [cadence, setCadence] = useState("monthly");
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => { setP(r.data); setImgIdx(0); });
  }, [slug]);

  // De-duplicate hero + gallery so identical URLs don't render twice
  const images = useMemo(() => {
    if (!p) return [];
    const raw = [p.hero_image, ...(p.gallery || [])].filter(Boolean);
    return Array.from(new Set(raw));
  }, [p]);

  if (!p) return <div className="max-w-[1400px] mx-auto px-6 py-32 text-center text-ink-500">Loading…</div>;

  const subDiscount = p.subscription_discount_pct || 10;
  const finalPrice = mode === "subscribe" ? Math.round(p.price * (1 - subDiscount / 100)) : p.price;

  const onAdd = () => {
    add(p, { quantity: qty, subscription: mode === "subscribe" ? cadence : null });
    toast.success(`${p.name} added to bag`);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 lg:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
        {/* Gallery — dedup'd; carousel only when multiple unique images */}
        <div className="lg:col-span-6">
          <div className="relative bg-cream-200 overflow-hidden aspect-square max-w-[460px] mx-auto lg:mx-0">
            {images[imgIdx] ? (
              <img
                src={images[imgIdx]}
                alt={p.name}
                loading="eager"
                decoding="async"
                className="w-full h-full object-cover"
                data-testid="pdp-hero-image"
              />
            ) : null}
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setImgIdx((i) => (i - 1 + images.length) % images.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-ink-900 w-9 h-9 flex items-center justify-center backdrop-blur"
                  aria-label="Previous image"
                  data-testid="pdp-image-prev"
                ><ChevronLeft size={16} /></button>
                <button
                  onClick={() => setImgIdx((i) => (i + 1) % images.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-ink-900 w-9 h-9 flex items-center justify-center backdrop-blur"
                  aria-label="Next image"
                  data-testid="pdp-image-next"
                ><ChevronRight size={16} /></button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((_, i) => (
                    <span key={i} className={`h-1 w-6 ${i === imgIdx ? "bg-ink-900" : "bg-white/70"}`} />
                  ))}
                </div>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-2 max-w-[460px] mx-auto lg:mx-0">
              {images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setImgIdx(i)}
                  className={`aspect-square bg-cream-200 overflow-hidden border-2 transition-colors ${i === imgIdx ? "border-ink-900" : "border-transparent hover:border-ink-300"}`}
                  data-testid={`pdp-thumb-${i}`}
                >
                  <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:col-span-6 lg:sticky lg:top-28 self-start">
          <div className="eyebrow">{p.category} · {p.size}</div>
          <h1 className="mt-3 font-display text-5xl leading-[1.05] text-ink-900">{p.name}</h1>
          <div className="mt-2 text-sm text-gold-500 tracking-wide">{p.subtitle}</div>

          <div className="mt-6 flex items-end gap-3">
            <div className="font-display text-3xl text-ink-900" data-testid="pdp-price">₹{finalPrice}</div>
            {p.compare_at_price && p.compare_at_price > finalPrice && (
              <div className="text-ink-300 line-through text-lg">₹{p.compare_at_price.toFixed(0)}</div>
            )}
            {mode === "subscribe" && (
              <div className="eyebrow text-olive-500 mb-1">Save {subDiscount}%</div>
            )}
          </div>

          <p className="mt-6 text-ink-700 leading-relaxed">{p.short_description}</p>

          {/* Mode */}
          <div className="mt-8 grid grid-cols-2 gap-2" role="tablist" aria-label="Purchase mode">
            <button
              onClick={() => setMode("onetime")}
              data-testid="mode-onetime"
              className={"p-4 border text-left transition-colors " + (mode === "onetime" ? "bg-ink-900 text-cream-100 border-ink-900" : "hairline text-ink-900 hover:bg-cream-200")}
            >
              <div className="eyebrow">One-time</div>
              <div className="mt-1 text-sm">₹{p.price.toFixed(0)}</div>
            </button>
            <button
              onClick={() => setMode("subscribe")}
              data-testid="mode-subscribe"
              className={"p-4 border text-left transition-colors " + (mode === "subscribe" ? "bg-ink-900 text-cream-100 border-ink-900" : "hairline text-ink-900 hover:bg-cream-200")}
            >
              <div className="eyebrow flex items-center gap-2">Subscribe · save {subDiscount}%</div>
              <div className="mt-1 text-sm">₹{Math.round(p.price * (1 - subDiscount / 100))}</div>
            </button>
          </div>

          {mode === "subscribe" && (
            <div className="mt-4">
              <label className="eyebrow block mb-2">Cadence</label>
              <Select value={cadence} onValueChange={setCadence}>
                <SelectTrigger className="rounded-none border-ink-900/30 bg-cream-50 h-12" data-testid="cadence-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-cream-50 border-ink-900/30" data-testid="cadence-content">
                  {CADENCES.map((c) => (
                    <SelectItem key={c.v} value={c.v} data-testid={`cadence-${c.v}`}>{c.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-2 text-[11px] text-ink-500">Skip, pause, or cancel any time from your account.</p>
            </div>
          )}

          {/* Qty + add */}
          <div className="mt-6 flex gap-3">
            <div className="flex items-center border hairline">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-12 flex items-center justify-center" data-testid="qty-decrease" aria-label="Decrease">
                <Minus size={14} />
              </button>
              <div className="w-10 text-center text-sm tabular-nums" data-testid="qty-value">{qty}</div>
              <button onClick={() => setQty((q) => q + 1)} className="w-11 h-12 flex items-center justify-center" data-testid="qty-increase" aria-label="Increase">
                <Plus size={14} />
              </button>
            </div>
            <button onClick={onAdd} className="flex-1 btn-primary-ink text-xs uppercase tracking-[0.28em]" data-testid="add-to-cart">
              Add — ₹{(finalPrice * qty).toFixed(0)}
            </button>
          </div>

          {/* Return policy */}
          <div className="mt-6 border-l-2 border-gold-500 pl-3 py-1 bg-cream-50" data-testid="pdp-non-returnable">
            <div className="eyebrow text-ink-900">Non-returnable</div>
            <p className="text-[12px] text-ink-700 mt-1 leading-snug">
              For hygiene and safety, this product cannot be returned or exchanged once delivered. Prices are inclusive of GST.
            </p>
          </div>

          {/* Certs */}
          <div className="mt-6 flex flex-wrap gap-2">
            {(p.certifications || []).map((c) => (
              <span key={c} className="text-[10px] tracking-[0.2em] uppercase border hairline px-3 py-1 text-ink-700">{c}</span>
            ))}
          </div>

          {/* Details accordion */}
          <div className="mt-10">
            <Accordion type="single" collapsible defaultValue="about" className="w-full">
              <AccordionItem value="about" className="border-t hairline">
                <AccordionTrigger className="py-4 text-sm">The formula</AccordionTrigger>
                <AccordionContent className="text-sm text-ink-700 leading-relaxed pb-4">
                  {p.long_description}
                  {p.benefits?.length > 0 && (
                    <ul className="mt-4 space-y-2">
                      {p.benefits.map((b) => (
                        <li key={b} className="flex items-start gap-2"><Check size={14} className="mt-1 text-gold-500 shrink-0" /><span>{b}</span></li>
                      ))}
                    </ul>
                  )}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="how" className="border-t hairline">
                <AccordionTrigger className="py-4 text-sm">How to use</AccordionTrigger>
                <AccordionContent className="text-sm text-ink-700 leading-relaxed pb-4">{p.how_to_use}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="ing" className="border-t hairline">
                <AccordionTrigger className="py-4 text-sm">Full ingredients</AccordionTrigger>
                <AccordionContent className="text-[13px] text-ink-700 leading-relaxed pb-4">{p.ingredients}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="warn" className="border-t border-b hairline">
                <AccordionTrigger className="py-4 text-sm">Care & warnings</AccordionTrigger>
                <AccordionContent className="text-sm text-ink-700 leading-relaxed pb-4">{p.warnings}</AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <ProductReviews slug={p.slug} />
        </div>
      </div>
    </div>
  );
}
